import logo from "../assets/logo.png";
import whitelogo from "../assets/white_logo.png";
import React, { useState, useLayoutEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

export default function Navbar() {
    const { isDark, toggleTheme } = useTheme();
    const [mobileNav, setMobileNav] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we're on the landing page
    const isLandingPage = location.pathname === "/";

    // Handle navigation - either to page or scroll to section
    const handleNavClick = (e, path, sectionId = null) => {
        e.preventDefault();
        
        if (sectionId && isLandingPage) {
            // If on landing page and has section ID, scroll to section
            const element = document.querySelector(sectionId);
            if (element) {
                const offset = 90;
                const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top, behavior: 'smooth' });
            }
        } else if (sectionId && !isLandingPage) {
            // If not on landing page but clicking section link, go to landing with hash
            navigate(`/${sectionId}`);
        } else {
            // Regular page navigation
            navigate(path);
        }
        
        setMobileNav(false);
    };

    const navRef = useRef(null);

    useLayoutEffect(() => {
        const nav = navRef.current;
        if (!nav) return;
        document.documentElement.style.setProperty(
            "--mobile-nav-offset",
            `${nav.offsetHeight}px`
        );
    }, [mobileNav]);

    const mobileLinks = [
        { label: "Home", path: "/", section: "#home" },
        { label: "About", path: "/about" },
        { label: "Properties", path: "/properties" },
        { label: "Services", path: "/services" },
        { label: "Blog", path: "/blog" },
        { label: "Privacy Policy", path: "/privacy-policy" },
        { label: "Terms & Conditions", path: "/terms-conditions" },
    ];

    return (
        <>
        <nav
            ref={navRef}
            id="main-nav"
            className="fixed w-full z-50 top-0 left-0 transition-all duration-300 backdrop-blur-xl bg-white/80 dark:bg-slate-900/85 dark:border-b dark:border-slate-700/60 shadow-md dark:shadow-black/20"
            aria-label="Main Navigation"
        >
            <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">

                {/* LEFT — LOGO */}
                <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
                    <img
                        src={isDark ? whitelogo : logo}
                        alt="Landmarks Realty"
                        className="h-14 w-auto transition-all duration-300 hover:scale-[1.05]"
                    />
                </div>

                {/* CENTER — NAV LINKS */}
                <div className="hidden lg:flex items-center gap-10 text-[15px] font-semibold tracking-wide text-slate-800 dark:text-slate-100">
                    <button 
                        onClick={(e) => handleNavClick(e, "/", "#home")} 
                        className={`hover:text-[#D83B29] transition duration-200 ${location.pathname === "/" ? "text-[#D83B29] dark:text-orange-300" : ""}`}
                    >
                        Home
                    </button>
                    
                    <button 
                        onClick={(e) => handleNavClick(e, "/about")} 
                        className={`hover:text-[#D83B29] transition duration-200 ${location.pathname === "/about" ? "text-[#D83B29] dark:text-orange-300" : ""}`}
                    >
                        About
                    </button>
                    
                    <button 
                        onClick={(e) => handleNavClick(e, "/properties")} 
                        className={`hover:text-[#D83B29] transition duration-200 ${location.pathname === "/properties" ? "text-[#D83B29] dark:text-orange-300" : ""}`}
                    >
                        Properties
                    </button>
                    
                    <button 
                        onClick={(e) => handleNavClick(e, "/services")} 
                        className={`hover:text-[#D83B29] transition duration-200 ${location.pathname === "/services" ? "text-[#D83B29] dark:text-orange-300" : ""}`}
                    >
                        Services
                    </button>
                    
                    <button 
                        onClick={(e) => handleNavClick(e, "/blog")} 
                        className={`hover:text-[#D83B29] transition duration-200 ${location.pathname === "/blog" || location.pathname.startsWith("/blog/") ? "text-[#D83B29] dark:text-orange-300" : ""}`}
                    >
                        Blog
                    </button>
                    
                    <button 
                        onClick={(e) => handleNavClick(e, "/privacy-policy")} 
                        className={`hover:text-[#D83B29] transition duration-200 ${location.pathname === "/privacy-policy" ? "text-[#D83B29] dark:text-orange-300" : ""}`}
                    >
                        Privacy Policy
                    </button>
                    
                    <button 
                        onClick={(e) => handleNavClick(e, "/terms-conditions")} 
                        className={`hover:text-[#D83B29] transition duration-200 ${location.pathname === "/terms-conditions" ? "text-[#D83B29] dark:text-orange-300" : ""}`}
                    >
                        Terms & Conditions
                    </button>
                </div>

                {/* RIGHT — THEME + CONTACT */}
                <div className="hidden lg:flex items-center gap-3">
                    <button
                        type="button"
                        onClick={toggleTheme}
                        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                        className="p-2.5 rounded-full border border-slate-200 dark:border-slate-600 bg-white/80 dark:bg-slate-800/80 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shadow-sm"
                    >
                        {isDark ? <Sun className="w-5 h-5" strokeWidth={2} /> : <Moon className="w-5 h-5" strokeWidth={2} />}
                    </button>
                    <Link
                        to="/contact"
                        className="px-6 py-2 rounded-full bg-gradient-to-r from-[#8B1E1E] to-[#D83B29] text-white shadow-lg hover:brightness-110 transition-all duration-300"
                    >
                        Contact
                    </Link>
                </div>

                {/* MOBILE — THEME + MENU */}
                <div className="lg:hidden flex items-center gap-2">
                    <button
                        type="button"
                        onClick={toggleTheme}
                        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                        className="p-2 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-slate-200/80 dark:border-slate-600 shadow-md text-slate-800 dark:text-slate-100"
                    >
                        {isDark ? <Sun className="w-5 h-5" strokeWidth={2} /> : <Moon className="w-5 h-5" strokeWidth={2} />}
                    </button>
                    <button
                        onClick={() => setMobileNav(v => !v)}
                        aria-label="Toggle menu"
                        className="relative p-2 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-slate-200/80 dark:border-slate-600 shadow-lg hover:shadow-xl transition-all duration-300 group"
                    >
                        <div className="w-6 h-6 flex flex-col justify-center items-center">
                            <span className={`block w-5 h-0.5 bg-gray-700 dark:bg-slate-200 transition-all duration-300 ${mobileNav ? 'rotate-45 translate-y-1' : '-translate-y-1'}`}></span>
                            <span className={`block w-5 h-0.5 bg-gray-700 dark:bg-slate-200 transition-all duration-300 ${mobileNav ? 'opacity-0' : 'opacity-100'}`}></span>
                            <span className={`block w-5 h-0.5 bg-gray-700 dark:bg-slate-200 transition-all duration-300 ${mobileNav ? '-rotate-45 -translate-y-1' : 'translate-y-1'}`}></span>
                        </div>
                    </button>
                </div>

            </div>

        </nav>

            {/* Mobile menu — outside nav to avoid inherited text colors */}
            {mobileNav && (
                <div
                    className="fixed inset-0 z-[60] lg:hidden"
                    onClick={() => setMobileNav(false)}
                    aria-hidden="true"
                >
                    <div className="absolute inset-0 bg-black/40" />
                    <div
                        className="mobile-menu-panel absolute left-0 right-0 shadow-inner"
                        style={{ top: "var(--mobile-nav-offset, 88px)" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 py-5 flex flex-col gap-3 text-[17px]">
                            {mobileLinks.map(({ label, path, section }) => (
                                <button
                                    key={label}
                                    type="button"
                                    onClick={(e) => handleNavClick(e, path, section ?? null)}
                                    className="mobile-menu-link"
                                >
                                    {label}
                                </button>
                            ))}

                            <Link
                                to="/contact"
                                onClick={() => setMobileNav(false)}
                                className="mt-3 py-3 rounded-lg bg-[#D83B29] text-white shadow-md hover:bg-[#b92f20] transition text-center block"
                            >
                                Contact
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}